import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearReportedOutage,
  isOutage,
  probeService,
  RECHECK_INTERVAL_MS,
  reportedServiceState,
  type ServiceState,
  subscribeServiceState,
} from './availability';

export interface ServiceStatus {
  /** `null` until the first probe answers: nothing may mount before that. */
  readonly state: ServiceState | null;
  /** A probe is in flight. Drives the "Verificando servidor…" line. */
  readonly checking: boolean;
  /** When the last probe answered. Browser clock, shown as information only. */
  readonly checkedAt: Date | null;
  /** The button. Guarded against a second click while one is in flight. */
  readonly recheck: () => void;
  /**
   * Bumped every time the service comes back.
   *
   * Used as a React `key`, which is what makes the return a REMOUNT rather than
   * a resume: see `AvailabilityGate`.
   */
  readonly generation: number;
}

/**
 * The whole client's answer to "may we run?".
 *
 * ## The order it happens in
 *
 * Probe first, mount second. Nothing - not Phaser, not the hunt, not the
 * inventory, not the store, not an interactive sign-in - is built before the
 * server has said it is serving. Mounting first and asking afterwards means a
 * canvas exists, a session bootstrap is already in flight, and the answer
 * arrives to a client that has started doing the thing it should not.
 *
 * ## Two ways in, and no polling in between
 *
 * A `503` from any authoritative route reports itself here synchronously, so
 * maintenance that starts mid-session is noticed by the request that hit it -
 * no timer, no extra traffic, and nothing to forget to wire up. The periodic
 * check exists only while a notice is already on screen, at most once every
 * 15 s, plus whatever the player asks for with the button.
 */
export function useServiceState(): ServiceStatus {
  const [state, setState] = useState<ServiceState | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [generation, setGeneration] = useState(0);
  /*
   * One probe at a time, whatever asks for it.
   *
   * A ref rather than the `checking` state: two clicks in the same frame both
   * read the same stale `false` and both fire. A ref is written before the
   * await and read by the next caller.
   */
  const inFlight = useRef(false);
  const mounted = useRef(true);
  // Whether the previous answer was an outage, so a return can be detected
  // without making the probe callback depend on the state it sets.
  const wasOutage = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const check = useCallback(async () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setChecking(true);
    try {
      const result = await probeService();
      if (!mounted.current) {
        return;
      }
      if (result.state === 'serving') {
        // A refusal reported from inside a session is about the last answer,
        // not a latch. The probe is what takes it back.
        clearReportedOutage();
        if (wasOutage.current) {
          /*
           * The service came back. Everything built before the outage is
           * suspect - a snapshot from a simulation the server may have reset, a
           * balance from a wallet it may have rewritten, a session it may have
           * ended - so the generation moves and the tree is rebuilt from
           * nothing rather than resumed.
           */
          setGeneration((current) => current + 1);
        }
      }
      wasOutage.current = isOutage(result.state);
      setState(result.state);
      setCheckedAt(result.at);
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setChecking(false);
      }
    }
  }, []);

  // The first probe, before anything else exists.
  useEffect(() => {
    void check();
  }, [check]);

  /*
   * A refusal from any authoritative route, in the same tick it happened.
   *
   * The subscription is set up before the first probe resolves on purpose: a
   * request that fails while the probe is still travelling is still a fact
   * about the service.
   */
  useEffect(
    () =>
      subscribeServiceState((reported) => {
        wasOutage.current = isOutage(reported);
        setState(reported);
        setCheckedAt(new Date());
      }),
    [],
  );

  // Whatever the API client last saw, applied on mount - a refusal may have
  // been reported before this hook existed.
  useEffect(() => {
    const already = reportedServiceState();
    if (isOutage(already)) {
      wasOutage.current = true;
      setState(already);
    }
  }, []);

  /*
   * The automatic re-check, and ONLY while a notice is on screen.
   *
   * While the game is serving there is no timer at all: this state changes a
   * few times a year, and a client that asked every few seconds would ask a
   * million times for every time it mattered.
   */
  useEffect(() => {
    if (state === null || !isOutage(state)) {
      return;
    }
    const timer = setInterval(() => {
      void check();
    }, RECHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [check, state]);

  const recheck = useCallback(() => {
    void check();
  }, [check]);

  return { state, checking, checkedAt, recheck, generation };
}
